import { Payload, getShippingCost } from 'api/shipping-cost'
import { Currencies } from 'constants/currencies'
import { useCurrencies } from 'contexts/hooks/useCurrencies'
import { useUserProfile } from 'hooks/useUserProfile'
import { useMemo } from 'react'
import { UseQueryOptions, useQuery } from 'react-query'
import { TradeType } from 'types'
import { isUK } from 'utils/currencies'

type Options = Omit<
  UseQueryOptions<number | undefined, unknown, number | undefined, any[]>,
  'queryKey' | 'queryFn' | 'refetchOnMount' | 'refetchOnWindowFocus'
> & { convertToUserCurrency?: boolean; tradeType?: TradeType }

/**
 * Determines the shipping cost for a given payload.
 */
export const useShippingCost = (payload: Payload, options?: Options) => {
  const { getConvertedValue } = useCurrencies()

  const { data, ...rest } = useQuery(
    [
      'shippingFee',
      payload.countryCode,
      payload.buyingPrice,
      payload.medium,
      payload.shipmentCategory,
      payload.size,
      payload.framed,
      payload.galleryName,
      payload.shipmentWeightOverride,
    ],
    async () => {
      if (options?.tradeType !== 'bid' && options?.tradeType !== 'buy') return 0
      const res = await getShippingCost(payload)

      if (!res) return 0

      if (typeof res === 'number') {
        return res / 100
      }

      if (res.error) {
        console.error(res.error.message)
        return 0
      }

      return 0
    },
    { ...options, refetchOnMount: true, refetchOnWindowFocus: false }
  )

  // if needed, convert to user currency and round the result
  const _data = useMemo(() => {
    let value = data

    if (options?.convertToUserCurrency) {
      value = getConvertedValue(data, {
        from: Currencies.GBP,
        to: Currencies.USER,
      })
    }

    if (!value) return 0

    return Math.ceil(value)
  }, [data, options?.convertToUserCurrency, getConvertedValue])

  return { data: _data, ...rest }
}

interface UseShippingCostsPayload extends Payload {
  buyingPriceGbp: number
}

export function useShippingCosts(payload: UseShippingCostsPayload) {
  const { data: userProfile, isLoading: isLoadingUserProfile } =
    useUserProfile()

  const {
    galleryName,
    shipmentWeightOverride,
    buyingPrice,
    buyingPriceGbp,
    framed,
    medium,
    shipmentCategory,
    size,
    countryCode,
    tradeType = 'bid',
  } = payload

  const country = countryCode || userProfile?.shippingAddress?.country
  const isGB = isUK(country)
  const { data: domesticShippingCost = 0 } = useShippingCost(
    {
      buyingPrice: buyingPrice,
      countryCode: 'GB',
      shipmentCategory: shipmentCategory,
      framed: framed,
      galleryName: galleryName,
      shipmentWeightOverride: shipmentWeightOverride,
    },
    {
      convertToUserCurrency: true,
      enabled: Boolean(isGB && buyingPrice),
      tradeType,
    }
  )
  const { data: shippingCost = 0 } = useShippingCost(
    {
      buyingPrice: buyingPriceGbp,
      countryCode: country,
      medium: medium,
      size: size,
      shipmentCategory: shipmentCategory,
      framed: framed,
      galleryName: galleryName,
      shipmentWeightOverride: shipmentWeightOverride,
    },
    {
      convertToUserCurrency: true,
      enabled: Boolean(
        userProfile?.shippingAddress?.country && !isGB && buyingPriceGbp
      ),
      tradeType,
    }
  )

  return { domesticShippingCost, shippingCost }
}
